/* eslint-disable @typescript-eslint/no-explicit-any -- DIRECT-239681 */

import type {
    ActionMatcherDescriptionCollection,
    ActionReducerMapBuilder,
    Action,
    CaseReducer,
    CaseReducers,
    TypedActionCreator,
} from './rtk-types';

type TypeGuard<T> = (value: any) => value is T;

/**
 * Код полностью перенесен из @reduxjs/toolkit, так как из @reduxjs/toolkit он не экспортируется.
 * Нужен для того, чтобы реализовать поведение extraReducers из createSlice внутри метода addExtraReducers.
 *
 * @see: https://github.com/reduxjs/redux-toolkit/blob/master/packages/toolkit/src/mapBuilders.ts#L126-L196
 */
export function executeReducerBuilderCallback<S>(
    builderCallback: (builder: ActionReducerMapBuilder<S>) => void
): [CaseReducers<S, any>, ActionMatcherDescriptionCollection<S>, CaseReducer<S, Action> | undefined] {
    const actionsMap: CaseReducers<S, any> = {};
    const actionMatchers: ActionMatcherDescriptionCollection<S> = [];

    let defaultCaseReducer: CaseReducer<S, Action> | undefined;

    const builder = {
        addCase(typeOrActionCreator: string | TypedActionCreator<any>, reducer: CaseReducer<S>) {
            if (process.env.NODE_ENV !== 'production') {
                /**
                 * to keep the definition by the user in line with actual behavior,
                 * we enforce `addCase` to always be called before calling `addMatcher`
                 * as matching cases take precedence over matchers
                 */
                if (actionMatchers.length > 0) {
                    throw new Error('`builder.addCase` should only be called before calling `builder.addMatcher`');
                }

                if (defaultCaseReducer) {
                    throw new Error('`builder.addCase` should only be called before calling `builder.addDefaultCase`');
                }
            }
            const type = typeof typeOrActionCreator === 'string' ? typeOrActionCreator : typeOrActionCreator.type;

            if (type in actionsMap) {
                throw new Error('addCase cannot be called with two reducers for the same action type');
            }

            actionsMap[type] = reducer;

            return builder;
        },
        addMatcher<A>(matcher: TypeGuard<A>, reducer: CaseReducer<S, A extends Action ? A : A & Action>) {
            if (process.env.NODE_ENV !== 'production') {
                if (defaultCaseReducer) {
                    throw new Error(
                        '`builder.addMatcher` should only be called before calling `builder.addDefaultCase`'
                    );
                }
            }

            actionMatchers.push({ matcher, reducer });

            return builder;
        },
        addDefaultCase(reducer: CaseReducer<S, Action>) {
            if (process.env.NODE_ENV !== 'production') {
                if (defaultCaseReducer) {
                    throw new Error('`builder.addDefaultCase` can only be called once');
                }
            }

            defaultCaseReducer = reducer;

            return builder;
        },
    };

    builderCallback(builder);

    return [actionsMap, actionMatchers, defaultCaseReducer];
}
